import type { CompactSetting } from '@shared/compaction'
import { DEFAULT_COMPACT_AT } from '@shared/compaction'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'

const OPTIONS: { value: CompactSetting; label: string; note: string }[] = [
  { value: 200_000, label: '200k', note: 'Leaner and cheaper: before the long-context price step (2× over 200k on the API key); sharper on very long chats.' },
  { value: 300_000, label: '300k', note: 'Balanced — the default: long chats keep most of their detail word for word.' },
  { value: 400_000, label: '400k', note: 'Longest verbatim memory of the chat (grok-4.6’s window is 500k).' },
  { value: 'window', label: 'Near the limit', note: 'Only when the model’s window is about to fill (90%).' },
]

/**
 * Where long chats auto-compact. Measured on the prompt the model itself reports each answer
 * (system prompt + tools + memory + the conversation) — the chat header's meter shows it.
 */
export function ContextSettings() {
  const settings = useStore((s) => s.settings)
  const update = useStore((s) => s.updateSettings)
  const cur = settings?.compactAt ?? DEFAULT_COMPACT_AT
  const note = OPTIONS.find((o) => o.value === cur)?.note
  return (
    <section id="settings-context" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Conversation context</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        When a chat’s context reaches this size, its older messages are summarized (the last 12 stay word for word) so the AI stays sharp. Measured on
        what the model actually receives — the meter in each chat’s header shows where it stands.
      </p>
      <div className="mt-3 inline-flex flex-wrap gap-0.5 rounded-lg bg-[var(--color-inset)] p-0.5">
        {OPTIONS.map((o) => (
          <button
            key={String(o.value)}
            onClick={() => void update({ compactAt: o.value === DEFAULT_COMPACT_AT ? null : o.value })}
            className={cn(
              'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
              cur === o.value ? 'bg-[var(--color-surface-3)] text-fg shadow-[var(--e-1)]' : 'text-fg-subtle hover:text-fg',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      {note && <p className="mt-2 text-[11.5px] text-fg-subtle">{note}</p>}
    </section>
  )
}
